import styled from '@emotion/styled';
import { Menu, MenuItem } from '@mui/material';
import Button from '@shared/components/core/button/CoreButton';
import DetailContentDialog from '@shared/components/core/dialog/DetailContentDialog';
import StyledTextField from '@shared/components/core/field/StyledTextField';
import ItemActionList, { IItemActionListItem } from '@shared/components/core/listing/ItemActionList';
import { BodySmall } from '@shared/components/core/text/Typography';
import TinyHeader from '@shared/components/core/tiny/TinyHeader';
import { skillModeOptions } from '@shared/constants/extensions/skillMode';
import {
    DEFAULT_SKILL_ACTIVATION,
    MAX_SKILL_DESCRIPTION_LENGTH,
    SkillActivation,
    SkillFile,
    SkillFileKind,
    SkillModel,
} from '@shared/models/skills/SkillModel';
import { slugifySkillName, validateSkillDescription, validateSkillName } from '@shared/models/skills/skillValidation';
import type { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExtensionModeToggle, { IExtensionModeOption } from '../ExtensionModeToggle';
import SkillBestPractices from './SkillBestPractices';
import SkillMarkdownEditor from './SkillMarkdownEditor';
import { SkillEditorData } from './skillEditorData';
import { SKILL_TEMPLATES, SkillTemplate } from './skillTemplates';

/** Localized label for a file kind, shown as a small tag next to each file. */
function kindLabel(t: TFunction<'common'>, kind: SkillFileKind): string {
    switch (kind) {
        case 'script':
            return t('skills.files.kind.scriptShort');
        case 'asset':
            return t('skills.files.kind.assetShort');
        default:
            return t('skills.files.kind.referenceShort');
    }
}

/** Seed values for a new skill (used by "copy an existing skill" and template selection). */
export interface SkillEditorSeed {
    name?: string;
    description?: string;
    keywords?: string[];
    body?: string;
    activation?: SkillActivation;
    /**
     * Pre-staged supporting files for create mode (e.g. reference files a generated draft brings in).
     * Copied into the new skill on save, exactly like files staged in the editor's own picker.
     */
    files?: { sourcePath: string; name: string; kind: SkillFileKind }[];
}

interface SkillEditorProps {
    editorMode: 'create' | 'edit';
    /** The skill to edit; omit to create a new one. */
    skill?: SkillModel;
    /** Prefill values for create mode (e.g. copied from another skill). Ignored when {@link skill} is set. */
    initial?: SkillEditorSeed;
    /** Injected persistence layer (desktop = IPC-backed; portal = REST-backed). */
    data: SkillEditorData;
    /**
     * Hide the built-in "reference files & scripts" section. Use when a host screen owns file selection
     * (e.g. the "Teach a Skill" review, which manages references as cards); pre-staged {@link SkillEditorSeed.files}
     * are still copied on save.
     */
    hideFileManager?: boolean;
    /**
     * Hide the "Start from a template" control (create mode only). Use when the body is already seeded
     * from somewhere else (e.g. the "Teach a Skill" draft), so a template picker would only invite the
     * user to overwrite good generated instructions.
     */
    hideTemplates?: boolean;
    /** Called after a successful save; receives the created/updated skill when available. */
    onDone: (skill?: SkillModel) => void;
    onCancel: () => void;
}

/**
 * Guided create/edit form for a skill. Validates the name and description against the Agent Skills spec
 * live (so a saved skill always loads at runtime), and pre-fills a best-practice SKILL.md body for new
 * skills. Non-technical-friendly: plain labels, helper text, and the always-visible best-practices panel.
 */
export default function SkillEditor({
    skill,
    initial,
    data,
    hideFileManager,
    hideTemplates,
    onDone,
    onCancel,
    editorMode,
}: SkillEditorProps) {
    const { t } = useTranslation('common');
    const isEdit = !!skill;
    const canManageFiles = data.capabilities.manageFiles;

    const [name, setName] = useState(skill?.name ?? initial?.name ?? '');
    const [description, setDescription] = useState(skill?.description ?? initial?.description ?? '');
    const [keywords, setKeywords] = useState((skill?.keywords ?? initial?.keywords ?? []).join(', '));
    const [body, setBody] = useState(initial?.body ?? '');
    // In create mode the body is ready immediately (empty or from a seed); in edit mode we load it below.
    const [bodyLoaded, setBodyLoaded] = useState(!isEdit);
    const [activation, setActivation] = useState<SkillActivation>(
        skill?.activation ?? initial?.activation ?? DEFAULT_SKILL_ACTIVATION
    );
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [templateId, setTemplateId] = useState<string | null>(null);
    const [templatesAnchor, setTemplatesAnchor] = useState<HTMLElement | null>(null);

    // Supporting files. In edit mode these live on disk (loaded below); in create mode they're staged in
    // memory and copied into the new skill after it's created (see handleSave).
    const [files, setFiles] = useState<SkillFile[]>([]);
    const [stagedFiles, setStagedFiles] = useState<{ sourcePath: string; name: string; kind: SkillFileKind }[]>(
        skill ? [] : (initial?.files ?? [])
    );
    const [addFilesAnchor, setAddFilesAnchor] = useState<HTMLElement | null>(null);

    const applyTemplate = (template: SkillTemplate) => {
        setTemplatesAnchor(null);
        setTemplateId(template.id);
        if (template.suggestedName) setName(template.suggestedName);
        setDescription(template.description);
        setKeywords(template.keywords.join(', '));
        setBody(template.body);
        if (template.activation) setActivation(template.activation);
    };

    // Load the existing skill's SKILL.md body for editing (the list payload omits it).
    useEffect(() => {
        if (!skill) return;
        let active = true;
        data.getBody(skill.id).then((loaded) => {
            if (active) {
                setBody(loaded ?? '');
                setBodyLoaded(true);
            }
        });
        return () => {
            active = false;
        };
    }, [skill]);

    // Load the existing skill's supporting files (edit mode only; native file management only).
    useEffect(() => {
        if (!skill || !canManageFiles || !data.listFiles) return;
        let active = true;
        data.listFiles(skill.id).then((f) => {
            if (active) setFiles(f);
        });
        return () => {
            active = false;
        };
    }, [skill]);

    const basename = (p: string) => p.split(/[\\/]/).pop() ?? p;

    const handleAddFiles = async (kind: SkillFileKind) => {
        setAddFilesAnchor(null);
        if (!data.pickFiles) return;
        const paths = await data.pickFiles(kind);
        if (!paths || paths.length === 0) return;
        if (skill) {
            try {
                for (const p of paths) {
                    await data.addFile?.(skill.id, p, kind);
                }
                if (data.listFiles) setFiles(await data.listFiles(skill.id));
            } catch (error) {
                setSaveError(error instanceof Error ? error.message : t('skills.files.addError'));
            }
        } else {
            setStagedFiles((prev) => [...prev, ...paths.map((p) => ({ sourcePath: p, name: basename(p), kind }))]);
        }
    };

    const handleDeleteFile = async (relPath: string) => {
        if (!skill || !data.deleteFile) return;
        await data.deleteFile(skill.id, relPath);
        if (data.listFiles) setFiles(await data.listFiles(skill.id));
    };

    const handleOpenFile = (relPath: string) => {
        if (skill) data.openFile?.(skill.id, relPath);
    };

    const nameCheck = validateSkillName(name);
    const descriptionCheck = validateSkillDescription(description);
    const nameError = name.length > 0 && !nameCheck.valid ? nameCheck.error : undefined;
    const descriptionError = description.length > 0 && !descriptionCheck.valid ? descriptionCheck.error : undefined;
    const canSave = nameCheck.valid && descriptionCheck.valid && !saving && bodyLoaded;

    const parsedKeywords = keywords
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean);

    /**
     * Saved files can be opened on disk; staged ones don't exist there yet, so they only offer Remove.
     * Staged rows key on source path + index because the same file can legitimately be staged twice.
     */
    const fileItems: IItemActionListItem[] = isEdit
        ? files.map((f) => ({
              id: f.relPath,
              label: f.name,
              title: f.relPath,
              icon: f.kind === 'script' ? 'code' : 'files',
              identifier: kindLabel(t, f.kind),
              identifierVariant: f.kind === 'asset' ? 'accent' : 'diminished',
              disabled: saving,
              actions: [
                  { icon: 'externalLink', label: t('skills.files.open'), onClick: () => handleOpenFile(f.relPath) },
                  { icon: 'close', label: t('skills.files.remove'), onClick: () => handleDeleteFile(f.relPath) },
              ],
          }))
        : stagedFiles.map((s, i) => ({
              id: `${s.sourcePath}-${i}`,
              label: s.name,
              title: s.sourcePath,
              icon: s.kind === 'script' ? 'code' : 'files',
              identifier: kindLabel(t, s.kind),
              identifierVariant: s.kind === 'asset' ? 'accent' : 'diminished',
              disabled: saving,
              actions: [
                  {
                      icon: 'close',
                      label: t('skills.files.remove'),
                      onClick: () => setStagedFiles((prev) => prev.filter((_, idx) => idx !== i)),
                  },
              ],
          }));

    const activationOptions: IExtensionModeOption<SkillActivation>[] = skillModeOptions(t)
        .filter((option) => option.value !== 'off')
        .map((option) => ({ ...option, value: option.value as SkillActivation }));

    const handleSave = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            let saved: SkillModel | undefined;
            if (skill) {
                saved = await data.updateSkill({
                    id: skill.id,
                    name,
                    description,
                    keywords: parsedKeywords,
                    body,
                    activation,
                });
            } else {
                const created = await data.createSkill({
                    name,
                    description,
                    keywords: parsedKeywords,
                    // Empty body → the service scaffolds a best-practice template.
                    body: body.trim() ? body : undefined,
                    activation,
                });
                saved = created;
                // Copy any files staged during creation into the new skill (native file mgmt only).
                if (data.addFile) {
                    for (const staged of stagedFiles) {
                        await data.addFile(created.id, staged.sourcePath, staged.kind);
                    }
                }
            }
            onDone(saved);
        } catch (error) {
            setSaveError(error instanceof Error ? error.message : t('skills.editor.saveError'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <DetailContentDialog
            open
            fullWidth
            maxWidth="md"
            scroll="paper"
            onClose={onCancel}
            onCancel={onCancel}
            onConfirm={handleSave}
            confirmDisabled={!canSave}
            cancelButtonText={t('cancel')}
            confirmButtonText={isEdit ? t('skills.editor.save') : t('skills.editor.create')}
            confirmIcon="check"
            error={saveError ?? undefined}
            title={editorMode === 'edit' ? t('skills.editTitle') : t('skills.newTitle')}>
            <Body>
                <SkillBestPractices />

                {!isEdit && !hideTemplates && (
                    <TemplateRow>
                        <Button
                            $size="small"
                            $color="secondary"
                            startIcon="fileText"
                            iconSize={14}
                            disabled={saving}
                            onClick={(e) => setTemplatesAnchor(e.currentTarget)}>
                            {t('skills.templates.start')}
                        </Button>
                        {templateId && <TemplateApplied>{t('skills.templates.applied')}</TemplateApplied>}
                        <Menu
                            anchorEl={templatesAnchor}
                            open={!!templatesAnchor}
                            onClose={() => setTemplatesAnchor(null)}>
                            {SKILL_TEMPLATES.map((template) => (
                                <MenuItem key={template.id} onClick={() => applyTemplate(template)}>
                                    <TemplateMenuItem>
                                        <TemplateTitle>{t(template.titleKey, template.titleFallback)}</TemplateTitle>
                                        <TemplateSummary>
                                            {t(template.summaryKey, template.summaryFallback)}
                                        </TemplateSummary>
                                    </TemplateMenuItem>
                                </MenuItem>
                            ))}
                        </Menu>
                    </TemplateRow>
                )}

                <Field>
                    <StyledTextField
                        fullWidth
                        size="small"
                        label={t('skills.editor.nameLabel')}
                        value={name}
                        placeholder={t('skills.editor.namePlaceholder')}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={() => setName((current) => slugifySkillName(current) || current)}
                        error={!!nameError}
                        helperText={nameError ?? t('skills.editor.nameHelp')}
                        disabled={saving}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                </Field>

                <Field>
                    <StyledTextField
                        label={t('skills.editor.descriptionLabel')}
                        fullWidth
                        multiline
                        minRows={2}
                        value={description}
                        placeholder={t('skills.editor.descriptionPlaceholder')}
                        onChange={(e) => setDescription(e.target.value.slice(0, MAX_SKILL_DESCRIPTION_LENGTH))}
                        error={!!descriptionError}
                        helperText={descriptionError ?? t('skills.editor.descriptionHelp')}
                        disabled={saving}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                </Field>

                <Field>
                    <StyledTextField
                        fullWidth
                        size="small"
                        label={t('skills.editor.keywordsLabel')}
                        value={keywords}
                        placeholder={t('skills.editor.keywordsPlaceholder')}
                        onChange={(e) => setKeywords(e.target.value)}
                        helperText={t('skills.editor.keywordsHelp')}
                        disabled={saving}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                </Field>
                <Field>
                    <ExtensionModeToggle<SkillActivation>
                        label={t('skills.editor.activationLabel')}
                        options={activationOptions}
                        value={activation}
                        onChange={setActivation}
                    />
                </Field>

                <Field>
                    <TinyHeader size="small" title={t('skills.editor.instructionsLabel')} />
                    <SkillMarkdownEditor
                        value={body}
                        onChange={setBody}
                        disabled={saving || !bodyLoaded}
                        placeholder={t('skills.editor.instructionsPlaceholder')}
                    />
                </Field>

                {canManageFiles && !hideFileManager && (
                    <Field>
                        <TinyHeader size="small" title={t('skills.files.label')} description={t('skills.files.help')} />
                        <ItemActionList
                            monospaceLabels
                            items={fileItems}
                            emptyText={t('skills.files.empty')}
                            addLabel={t('skills.files.add')}
                            addDisabled={saving}
                            onAdd={(e) => setAddFilesAnchor(e.currentTarget)}
                        />
                        <Menu anchorEl={addFilesAnchor} open={!!addFilesAnchor} onClose={() => setAddFilesAnchor(null)}>
                            <MenuItem onClick={() => handleAddFiles('reference')}>
                                {t('skills.files.kind.reference')}
                            </MenuItem>
                            <MenuItem onClick={() => handleAddFiles('script')}>
                                {t('skills.files.kind.script')}
                            </MenuItem>
                            <MenuItem onClick={() => handleAddFiles('asset')}>{t('skills.files.kind.asset')}</MenuItem>
                        </Menu>
                    </Field>
                )}
            </Body>
        </DetailContentDialog>
    );
}

/**
 * The gap between fields is deliberately wider than the gap inside one (label → input → helper): a
 * helper has to sit closer to the field it describes than to the next field's label, or it reads as
 * loose body copy between the two.
 */
const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding-top: 0.5rem;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
`;

const TemplateRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

const TemplateApplied = styled(BodySmall)`
    color: ${(props) => props.theme.color.textBody};
`;

const TemplateMenuItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    max-width: 20rem;
    white-space: normal;
`;

const TemplateTitle = styled.span`
    font-size: 0.8125rem;
    font-weight: 600;
`;

const TemplateSummary = styled.span`
    font-size: 0.6875rem;
    color: ${(props) => props.theme.color.textBody};
`;
